/**
 * Shared auth types.
 *
 * These shapes are the contract between this frontend lane and the auth
 * service. They must stay byte for byte identical to the server side
 * definitions, so change them in both places or not at all.
 */

export interface AuthUser {
  id: string; // opaque, not the provider id
  email: string;
  /** Derived server side from firstName and lastName. Never sent back up. */
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  createdAt: string; // ISO
  /**
   * Sign-in methods this account can currently use. "email" is always present:
   * a magic link is deliverable to the account address by definition, which is
   * also why there is no "disconnect" for it.
   */
  connections: AuthConnection[];
}

export type AuthConnection = "email" | "google";

/**
 * One row of the active sessions table.
 *
 * `id` is a public per-session handle minted by the server. It is not the
 * session id and not a hash of one: neither of those ever reaches this app.
 * `device` and `location` are coarse labels the server derived at sign-in, so
 * there is no raw user agent or IP address here to render or to leak.
 */
export interface AuthSessionRecord {
  id: string;
  device: string;
  location: string;
  createdAt: string; // ISO
  lastActiveAt: string; // ISO
  /** True for the session this browser is using right now. */
  current: boolean;
}

export interface OnboardingState {
  completed: boolean;
  role: string | null; // founder | marketer | creator | product | engineer | designer | student | other
  source: string | null; // social | community | ai-tools | friend | google | newsletter | product-hunt | other
  sourceDetail: string | null; // linkedin | chatgpt | discord | free text, etc
  intent: string | null; // talking-head | explainer | product-demo | launch | vlog | montage | podcast | ad | other
}

/** What GET /auth/me returns when there is a live session. */
export interface AuthSession {
  user: AuthUser;
  onboarding: OnboardingState;
}

/**
 * What POST /auth/email/verify returns on a correct code.
 *
 * It is a live session plus one extra bit: `isNewUser` is true only when this
 * verify is what created the account. The UI uses it to steer a first-time
 * visitor into onboarding, but it never gates access on its own: a returning
 * user with `completed` onboarding still lands straight in the app, and the
 * name step keys off a missing name rather than this flag, so the two agree.
 */
export interface EmailOtpVerifyResult extends AuthSession {
  isNewUser: boolean;
}

/**
 * The server answered, and the answer was not a success.
 *
 * The distinction from AuthNetworkError is load bearing: only a 401 here is
 * proof that the session is gone. Everything else (a 500, a 429, a request
 * that never landed) tells us nothing about whether the user is signed in,
 * so it must never de-authenticate the UI.
 */
export class AuthApiError extends Error {
  readonly kind = "api" as const;
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.name = "AuthApiError";
    this.status = status;
    this.code = code;
  }
}

/**
 * The request never produced a server verdict: DNS failure, offline, CORS
 * rejection, or our own client side timeout. Callers must treat this as
 * "unknown", never as "signed out".
 */
export class AuthNetworkError extends Error {
  readonly kind = "network" as const;
  readonly timedOut: boolean;

  constructor(message: string, timedOut: boolean, cause?: unknown) {
    super(message, { cause });
    this.name = "AuthNetworkError";
    this.timedOut = timedOut;
  }
}

export type AuthError = AuthApiError | AuthNetworkError;

export function isAuthApiError(value: unknown): value is AuthApiError {
  return value instanceof AuthApiError;
}

export function isAuthNetworkError(value: unknown): value is AuthNetworkError {
  return value instanceof AuthNetworkError;
}

/** True only when the server explicitly rejected the session. */
export function isUnauthorized(value: unknown): boolean {
  return isAuthApiError(value) && value.status === 401;
}

/**
 * Normalize anything thrown by the client into an AuthError.
 *
 * Unexpected throws (a bug in our own parsing, say) are folded into
 * AuthNetworkError on purpose: from the session's point of view they are the
 * same situation as a request that never landed, meaning no server verdict,
 * so do not sign the user out.
 */
export function toAuthError(value: unknown): AuthError {
  if (isAuthApiError(value) || isAuthNetworkError(value)) return value;
  const message = value instanceof Error ? value.message : String(value);
  return new AuthNetworkError(`unexpected auth failure: ${message}`, false, value);
}
